import { create } from "@bufbuild/protobuf";

import { SourceType } from "@gx/ts-contracts/services/database/models/public_pb";
import {
  CreateSourceRequest,
  CreateSourceRequestSchema,
} from "@gx/ts-contracts/services/source/api/source_pb";
import {
  SourceBrowserbaseConfiguration,
  SourceBrowserbaseConfigurationSchema,
} from "@gx/ts-contracts/services/source/resources/source_configuration/browserbase/browserbase_pb";
import { SourceConfigurationSchema } from "@gx/ts-contracts/services/source/resources/source_configuration/source_configuration_pb";

import {
  CREATE_SOURCE_BROWSERBASE_API_KEY_REQUIRED_ERROR,
  CREATE_SOURCE_BROWSERBASE_INVALID_URL_ERROR,
  CREATE_SOURCE_BROWSERBASE_PROJECT_ID_REQUIRED_ERROR,
  CREATE_SOURCE_BROWSERBASE_URL_REQUIRED_ERROR,
  SOURCE_BROWSERBASE_CONFIGURATION_CASE,
} from "@/pages/sources/components/create/sources/browserbase/constants";
import { CreateSourceBrowserbaseState } from "@/pages/sources/components/create/sources/browserbase/types";
import {
  isCreateSourceNameProvided,
  isCreateSourceValueProvided,
} from "@/pages/sources/components/create/sources/utils";

import { isValidUrl } from "@/utils/urls";

export function getCreateSourceBrowserbaseUrlError(
  state: CreateSourceBrowserbaseState,
): string | null {
  if (!state.shouldShowErrors) {
    return null;
  }

  if (!isCreateSourceValueProvided(state.configuration.rootUrl)) {
    return CREATE_SOURCE_BROWSERBASE_URL_REQUIRED_ERROR;
  }

  return isValidUrl(state.configuration.rootUrl)
    ? null
    : CREATE_SOURCE_BROWSERBASE_INVALID_URL_ERROR;
}

export function isCreateSourceBrowserbaseGalaxyManagedCredentials(
  state: CreateSourceBrowserbaseState,
): boolean {
  return state.configuration.isGalaxyManagedCredentials;
}

export function getCreateSourceBrowserbaseApiKeyError(
  state: CreateSourceBrowserbaseState,
): string | null {
  if (!state.shouldShowErrors) {
    return null;
  }

  if (
    !isCreateSourceBrowserbaseGalaxyManagedCredentials(state) &&
    !isCreateSourceValueProvided(state.configuration.apiKey)
  ) {
    return CREATE_SOURCE_BROWSERBASE_API_KEY_REQUIRED_ERROR;
  }

  return null;
}

export function getCreateSourceBrowserbaseProjectIdError(
  state: CreateSourceBrowserbaseState,
): string | null {
  if (!state.shouldShowErrors) {
    return null;
  }

  if (
    !isCreateSourceBrowserbaseGalaxyManagedCredentials(state) &&
    !isCreateSourceValueProvided(state.configuration.projectId)
  ) {
    return CREATE_SOURCE_BROWSERBASE_PROJECT_ID_REQUIRED_ERROR;
  }

  return null;
}

export function isCreateSourceBrowserbaseValid(state: CreateSourceBrowserbaseState): boolean {
  if (!isCreateSourceNameProvided(state)) {
    return false;
  }

  if (
    !isCreateSourceValueProvided(state.configuration.rootUrl) ||
    !isValidUrl(state.configuration.rootUrl)
  ) {
    return false;
  }

  if (
    !isCreateSourceBrowserbaseGalaxyManagedCredentials(state) &&
    !isCreateSourceValueProvided(state.configuration.apiKey)
  ) {
    return false;
  }

  return true;
}

export function buildCreateSourceBrowserbaseRequest(
  sourceName: string,
  configuration: SourceBrowserbaseConfiguration,
): CreateSourceRequest {
  return create(CreateSourceRequestSchema, {
    name: sourceName.trim(),
    type: SourceType.BROWSERBASE,
    configuration: create(SourceConfigurationSchema, {
      configuration: {
        case: SOURCE_BROWSERBASE_CONFIGURATION_CASE,
        value: create(SourceBrowserbaseConfigurationSchema, {
          rootUrl: configuration.rootUrl.trim(),
          prompt: configuration.prompt,
          scrapeLimit: configuration.scrapeLimit,
          isGalaxyManagedCredentials: configuration.isGalaxyManagedCredentials,
          projectId: configuration.projectId,
          apiKey: configuration.apiKey,
        }),
      },
    }),
  });
}
